import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Crown, Camera, Trophy, ArrowLeft } from "lucide-react";

const GLYPHS = "アイウエオカキクケコサシスセソ0123456789$+-*/=%#PICKLEBULLPEN";

const GL_CSS = `
.gl-placard{animation:glPanDown 1.1s cubic-bezier(.22,1,.36,1) both}
@keyframes glPanDown{0%{transform:translateY(-108%);opacity:0}55%{opacity:1}100%{transform:translateY(0);opacity:1}}
.gl-etch{opacity:0;animation:glEtch .6s ease-out forwards}
@keyframes glEtch{0%{opacity:0;filter:blur(6px) brightness(3.2)}60%{filter:blur(0) brightness(1.8)}100%{opacity:1;filter:blur(0) brightness(1)}}
@keyframes glGlow{0%,100%{box-shadow:0 0 28px rgba(212,175,55,.28)}50%{box-shadow:0 0 46px rgba(212,175,55,.5)}}
.gl-frame{animation:glGlow 3.2s ease-in-out infinite}
`;

function RainCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf, cols, drops;
    const setup = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      cols = Math.ceil(canvas.width / 16);
      drops = Array.from({ length: cols }, () => Math.random() * -(canvas.height / 16));
    };
    setup();
    const draw = () => {
      ctx.fillStyle = "rgba(6,5,2,.14)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = "12px monospace";
      for (let i = 0; i < cols; i++) {
        ctx.fillStyle = "rgba(212,175,55,.35)";
        ctx.fillText(GLYPHS[Math.floor(Math.random() * GLYPHS.length)], i * 16, drops[i] * 16);
        drops[i] = drops[i] * 16 > canvas.height && Math.random() > 0.975 ? 0 : drops[i] + 0.5;
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", setup);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", setup); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-50 pointer-events-none" />;
}

const E = ({ d, children, className = "" }) => (
  <div className={`gl-etch ${className}`} style={{ animationDelay: `${d}s` }}>{children}</div>
);

export default function GoldLetter() {
  const { lid } = useParams();
  const [letter, setLetter] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/letters/${lid}`).then((r) => setLetter(r.data)).catch(() => setError("This letter isn't available."));
  }, [lid]);

  if (error) return <div className="min-h-[70vh] flex items-center justify-center text-zinc-500 font-display uppercase">{error}</div>;
  if (!letter) return <div className="min-h-[70vh] flex items-center justify-center text-zinc-600 animate-pulse">Decrypting transmission…</div>;

  const paragraphs = letter.body.split("\n\n");
  return (
    <div className="relative min-h-[85vh] overflow-hidden -m-4 sm:-m-6 p-3 sm:p-6" style={{ background: "#060502" }} data-testid="gold-letter-page">
      <style>{GL_CSS}</style>
      <RainCanvas />
      <div className="relative z-10 max-w-2xl mx-auto">
        <Link to="/tournaments" className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-yellow-400 transition-colors mb-3" data-testid="letter-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Tournaments
        </Link>
        <div className="gl-placard gl-frame border-2 border-yellow-500/80 bg-black/90 backdrop-blur-sm p-1">
          <div className="border border-yellow-500/40 p-6 sm:p-8">
            <E d={0.9} className="text-center mb-5">
              <Crown className="w-10 h-10 text-yellow-400 mx-auto mb-2" style={{ filter: "drop-shadow(0 0 14px rgba(212,175,55,.8))" }} />
              <div className="text-[10px] font-black uppercase tracking-[0.4em] text-yellow-400">A Gold Letter · Pickle Bullpen</div>
              <div className="font-display font-black uppercase text-xl sm:text-2xl mt-2 leading-tight text-white" data-testid="letter-subject">{letter.subject}</div>
            </E>
            <div className="space-y-4 text-[15px] leading-relaxed text-zinc-200" style={{ fontFamily: "Georgia, serif" }} data-testid="letter-body">
              {paragraphs.map((p, i) => (
                <E key={i} d={1.2 + i * 0.35} className="whitespace-pre-line">{p}</E>
              ))}
            </div>
            <E d={1.2 + paragraphs.length * 0.35 + 0.2} className="flex flex-wrap gap-3 mt-7">
              {letter.gallery_url && (
                <a href={letter.gallery_url} data-testid="letter-gallery-btn"
                  className="flex items-center gap-2 bg-yellow-500 text-black text-[10px] font-black uppercase tracking-widest px-4 py-3 hover:bg-yellow-400 transition-colors">
                  <Camera className="w-4 h-4" /> Relive it in the Gallery
                </a>
              )}
              <Link to="/tournaments" data-testid="letter-events-btn"
                className="flex items-center gap-2 border border-[#22D3EE]/70 text-[#22D3EE] text-[10px] font-black uppercase tracking-widest px-4 py-3 hover:bg-[#22D3EE]/10 transition-colors">
                <Trophy className="w-4 h-4" /> Claim your next event
              </Link>
            </E>
          </div>
        </div>
      </div>
    </div>
  );
}
